import React, { useCallback, useEffect } from 'react';
import { CumulativePlotView } from './CumulativePlotView';
import { createStructuredSelector } from 'reselect';
import { RouteComponentProps } from 'react-router-dom';
import {
  fetchCumulativePlotData,
  selectCumulativePlotData,
  unmountCumulativePlot,
} from 'ducks/plot/cumulative';
import { FullState } from 'main/reducers';
import { useDispatch, useSelector } from 'react-redux';
import { sortBy } from 'lodash';
import { CumulativePlotZoomParams } from 'components/plots/CumulativePlot';
import { parseQueryParamFromRouterProps, setQueryParams } from 'util/routing';

interface CumulativePlotSettingsFromUrl {
  observationPointCode: string;
  itemNumber: number;
  timePeriods: number[];
  xDomain: number[] | null;
  yDomain: number[] | null;
}

interface UrlMatchProps {
  op?: string;
  item?: string;
  years?: string;
}

type OwnProps = RouteComponentProps<UrlMatchProps>;

export const getCumulativePlotSettingsFromUrl = createStructuredSelector<
  OwnProps,
  CumulativePlotSettingsFromUrl
>({
  observationPointCode: (props) => props.match.params.op || '',
  itemNumber: (props) => +(props.match.params.item || 0),
  timePeriods: (props) =>
    props.match.params.years
      ? sortBy(props.match.params.years.split(',').map((year) => +year))
      : [],
  xDomain: (props) =>
    parseQueryParamFromRouterProps(props, 'xDomain', null)
      ?.split('~')
      .map((i) => +i) ?? null,
  yDomain: (props) =>
    parseQueryParamFromRouterProps(props, 'yDomain', null)
      ?.split('~')
      .map((i) => +i) ?? null,
});

export function CumulativePlotScreen(props: OwnProps) {
  const settingsFromUrl = getCumulativePlotSettingsFromUrl(props);

  const { observationPointCode, itemNumber, timePeriods } = settingsFromUrl;

  const dispatch = useDispatch();

  const plotState = useSelector((state: FullState) =>
    selectCumulativePlotData(state, 0)
  );

  useEffect(
    () => () => {
      dispatch(unmountCumulativePlot());
    },
    [dispatch]
  );

  useEffect(() => {
    if (!observationPointCode || !itemNumber || timePeriods.length === 0) {
      return;
    }
    dispatch(
      fetchCumulativePlotData(observationPointCode, itemNumber, timePeriods)
    );
  }, [dispatch, observationPointCode, itemNumber, timePeriods]);

  const handleZoom = useCallback(
    (zoom: CumulativePlotZoomParams) => {
      setQueryParams(
        { history: props.history, location: props.location },
        {
          xDomain: zoom.xDomain.join('~'),
          yDomain: zoom.yDomain.join('~'),
        }
      );
    },
    [props.history, props.location]
  );

  const handleResetZoom = useCallback(() => {
    setQueryParams(
      { history: props.history, location: props.location },
      { xDomain: null, yDomain: null }
    );
  }, [props.history, props.location]);

  const errorMessage = plotState?.errorMessage ?? null;
  const isLoading = plotState?.isLoading ?? false;

  const observationPoint = plotState?.observationPoints?.[0] ?? null;
  const plotReadings = plotState?.plotReadings ?? null;

  const zoomArea: CumulativePlotZoomParams | null =
    settingsFromUrl.xDomain && settingsFromUrl.yDomain
      ? {
          xDomain: settingsFromUrl.xDomain,
          yDomain: settingsFromUrl.yDomain,
        }
      : null;

  return (
    <CumulativePlotView
      observationPoint={observationPoint}
      cumulativePlotReadings={plotReadings}
      isLoading={isLoading}
      errorMessage={errorMessage}
      itemNumber={itemNumber}
      timePeriods={timePeriods}
      zoomArea={zoomArea}
      onZoom={handleZoom}
      onResetZoom={handleResetZoom}
    />
  );
}
